import { Quaternion, Vector3 } from '@babylonjs/core/Maths/math.vector';
import type { AbstractMesh } from '@babylonjs/core/Meshes/abstractMesh';
import type { Scene } from '@babylonjs/core/scene';

import { CELL_SIZE, DEFAULT_SEARCH_RADIUS_CELLS, occupiedCellKeys, snapPlacement } from '../core/GridSnap';
import type { Point3 } from '../core/RoomFit';
import type { Grabber } from '../input/Grabbers';
import type { GrabberHub } from '../input/Grabbers';
import type { RoomHub } from '../net/roomHub';
import type { TransformStreamer } from '../net/TransformStreamer';
import { nodeWithinReach } from './GrabReach';
import type { PlacementManager } from './PlacementManager';
import type { RoomState } from '../room/RoomState';
import type { RoomView } from '../room/RoomView';
import type { ToolMode } from './ToolMode';

/**
 * Grab = lock = permission to edit.
 *
 * One mechanism serving two problems. In VR you constantly bump into things, so nothing may
 * change without a deliberate act; and in a shared room two people must not fight over the
 * same shape. A possession lock answers both: you cannot edit what you are not holding, and
 * you cannot hold what someone else has.
 *
 * There is no undo in v1, which is exactly why the bar for "this counts as an edit" is set
 * here rather than left to chance.
 *
 * **Grabbing reads the dominant trigger, not the grip** (plan/mode-hierarchy.md, "Grabbing
 * moves from the dominant grip to the dominant trigger; near/far is gone" — settled
 * 2026-08-10). The grip used to be the whole story: squeeze near a node to take hold of it,
 * pull the trigger from across the room to aim a connection at it, two physical buttons so one
 * press could never mean both. That split existed to keep grabbing and connecting from
 * competing for the same press, and it is no longer needed for that: Link is now its own mode,
 * and grabbing and connecting are never live in the same mode at once — Build's trigger only
 * ever grabs (`isToolActive` refuses a grab everywhere else), Link's only ever links
 * (`ConnectionDrawer.tick`, gated on `isLinkMode`) — so which mode you are in already answers
 * what a press means, structurally, with nothing left for a proximity tie-break to arbitrate.
 * `nodeWithinReach` (`GrabReach.ts`) is still what finds *which* node a grab in Build mode
 * lands on; it briefly had a second caller in `ConnectionDrawer` too, as an interim stopgap
 * shipped ahead of Link mode existing (see that file's own module doc, and
 * plan/mode-hierarchy.md's "Sequencing") — retired once Link shipped, since the mode split
 * makes that tie-break unreachable rather than merely redundant. The dominant grip is free —
 * nothing here reads it; it is spent on mode switching instead
 * (`AuthoringController.tickToolCycle`).
 */

/**
 * How far from the off hand a held node's own bounding sphere may sit and still count as
 * "released into the bin" (plan/ui-improvements.md, item 3 — the owner: "I'd rather have the
 * left hand turn into a trash can to act as a delete button"). The same shape of test as
 * `isWithinGrabReach` (`GrabReach.ts`) — a fixed margin on top of the node's own radius, so a
 * larger node is easier to drop in than a tiny one, which matches how dropping something into a
 * real bin actually works.
 */
const TRASH_REACH = 0.1;

/** Hold the gesture this long before it counts, so a passing pinch is not a grab. */
const GRAB_INTENT_MS = 120;

/**
 * Movement below this between grab and release commits nothing. Picking a node up and
 * putting it back must not count as having moved it.
 */
const MOVEMENT_DEADBAND = 0.015;

interface Held {
  nodeId: string;
  grabberId: string;
  mesh: AbstractMesh;

  /** Offset from grabber to node at the moment of grabbing, in grabber space. */
  grabOffset: Vector3;
  grabRotationOffset: Quaternion;

  /** Room space — the mesh's own position/rotation at the moment of grabbing, compared against
   *  where it ends up to decide whether anything actually moved (the `MOVEMENT_DEADBAND` check
   *  in `release()`). */
  startPosition: Vector3;
  startRotation: Quaternion;

  /**
   * Canonical — the node's own server-truth position at the moment of grabbing, read directly
   * from `RoomState` rather than derived from `startPosition` through the room fit. `release()`
   * resends this verbatim when nothing moved, instead of converting `startPosition` back to
   * canonical: `toRoom`/`toCanonical` are exact inverses mathematically, but a floating-point
   * round trip through them is not guaranteed to reproduce the original bit-for-bit, and "grab
   * and put back, repeatedly, drifts" is exactly the failure this task's own success criteria
   * name (plan/diagram-extraction.md, A4 — "a round trip must be lossless enough that a node
   * does not drift"). Resending the value that was never touched is the only way to actually
   * guarantee that, rather than merely making it likely.
   */
  startCanonicalPosition: Point3;
}

interface PendingGrab {
  nodeId: string;
  grabberId: string;
  since: number;
}

/**
 * Whether `held` is sitting in the off hand's own trash can *right now* — read only at the
 * moment of release (see `release()`'s own call site), never continuously while carrying. That
 * is the entire safety property the owner asked for: "Trash should only activate on release."
 * A node passed through the bin's own volume on its way somewhere else must not delete anything
 * — only letting go while it is there does. `off` is `undefined` when no off-hand grabber is
 * currently present (controller disconnected, hands-only session with the off hand out of
 * tracking), which this treats as "nowhere near a bin that is not there," not as a crash.
 *
 * The same shape as `isWithinGrabReach` above — a fixed margin (`TRASH_REACH`) on top of the
 * held node's own world bounding-sphere radius — rather than a fixed point-to-point distance,
 * so a bigger node is easier to drop in than a tiny one, matching a real bin.
 */
function isOverTrash(off: Grabber | undefined, mesh: AbstractMesh): boolean {
  if (!off) {
    return false;
  }

  mesh.computeWorldMatrix(true);
  const bounding = mesh.getBoundingInfo().boundingSphere;
  return Vector3.Distance(off.position, bounding.centerWorld) <= bounding.radiusWorld + TRASH_REACH;
}

// spec: SPEC-072
// spec: SPEC-074
export class PossessionManager {
  private held: Held | null = null;
  private pending: PendingGrab | null = null;

  /** Set while a grab is being requested, so a slow server cannot start two. */
  private requesting = false;

  /** Fired when a grab is refused, with the holder's colour, for the caller to surface. */
  onGrabRefused: ((nodeId: string, holderColor: string | null) => void) | null = null;

  onGrabbed: ((nodeId: string) => void) | null = null;
  onReleased: ((nodeId: string) => void) | null = null;

  /** Surfaced when the grid search on release finds no free cell nearby — see `release()`'s
   *  own doc comment. */
  onNotice: ((message: string) => void) | null = null;

  constructor(
    private readonly scene: Scene,
    private readonly grabbers: GrabberHub,
    private readonly state: RoomState,
    private readonly view: RoomView,
    private readonly hub: RoomHub,
    private readonly streamer: TransformStreamer,

    // Read directly, not through an injected veto: `placement`/`toolMode` are siblings this
    // class did not used to know about, wired in the same way `DrawingManager` already takes
    // `ToolMode` (that file, not a callback, is the precedent this follows). Carrying a tray
    // shape or being in draw mode puts the dominant hand's grip to work on something else
    // through a path `nodeWithinReach` cannot otherwise tell apart from a node sitting exactly
    // where the hand already is, so a fresh grab has to know about both. This replaces
    // `suppressGrab`, a boolean the caller (previously AuthoringController) had to keep
    // re-deriving from these same two facts and re-assign in; see plan/history/control-modes.md,
    // "suppressGrab is deleted, not reimplemented," for why a second, purpose-built mechanism
    // watching the first was the thing worth removing, not merely renaming.
    private readonly placement: PlacementManager,
    private readonly toolMode: ToolMode,

    // A third sibling, `connections: ConnectionDrawer`, used to be required here too — a
    // grabber already busy drawing a connection had to be vetoed from also starting a pending
    // grab, back when both gestures could be live in the same (build) mode's trigger at once.
    // Removed once Link became its own mode: `ConnectionDrawer.tick` now only ever runs while
    // `toolMode.isLinkMode`, and `considerGrab` below already refuses a grab in every mode but
    // build (`toolMode.isToolActive`) — the two conditions are mutually exclusive by
    // construction, so a grabber can never be mid-drag while this method's own `isToolActive`
    // check would let it through. Confirmed unused rather than assumed: TypeScript's
    // `noUnusedParameters` would have failed the build the moment nothing read it any more.
  ) {}

  get heldNodeId(): string | null {
    return this.held?.nodeId ?? null;
  }

  start(): () => void {
    const observer = this.scene.onBeforeRenderObservable.add(() => this.tick(performance.now()));

    return () => this.scene.onBeforeRenderObservable.remove(observer);
  }

  tick(nowMs: number): void {
    const grabbers = this.grabbers.grabbers;

    if (this.held !== null) {
      this.trackHeld(grabbers);
      return;
    }

    this.considerGrab(grabbers, nowMs);
  }

  /** Drops whatever is held. Used on disconnect and teardown. */
  releaseImmediately(): void {
    if (this.held === null) {
      return;
    }

    void this.release();
  }

  private considerGrab(grabbers: Grabber[], nowMs: number): void {
    if (this.requesting) {
      return;
    }

    // Dominant hand only. The off hand's grip belongs to the tray, and letting both grab was
    // half of why every input felt like it did the same thing.
    for (const grabber of grabbers.filter((candidate) => candidate.role === 'dominant')) {
      // A shape mid-carry, or the dominant hand busy with a non-build tool (draw, scan, or —
      // now — link): the dominant trigger already means something else, so a fresh grab is
      // never considered here — read straight off the classes that would know, not a flag any
      // of them has to remember to set. `isToolActive` rather than `isDrawMode` alone since
      // every non-build mode reserves the dominant hand this same way (ToolMode.isToolActive's
      // own doc) — this is also what makes a grab structurally impossible while
      // `ConnectionDrawer` is mid-drag in Link mode, without needing to ask it directly any
      // more (see this class's own constructor doc for what used to check that separately).
      if (!grabber.far.pressed || this.placement.isCarrying || this.toolMode.isToolActive) {
        if (this.pending?.grabberId === grabber.id) {
          this.pending = null;
        }

        continue;
      }

      const nodeId = nodeWithinReach(grabber, this.state, this.view);

      if (nodeId === null) {
        if (this.pending?.grabberId === grabber.id) {
          this.pending = null;
        }

        continue;
      }

      // Someone else's. Refuse locally rather than asking the server, so the hand passes
      // through without a round trip — it is already faded in their colour, which says why.
      if (this.state.isHeldByOther(nodeId)) {
        if (this.pending?.nodeId !== nodeId) {
          this.pending = null;
          this.onGrabRefused?.(nodeId, this.state.holderColor(nodeId));
        }

        continue;
      }

      if (this.pending === null || this.pending.nodeId !== nodeId || this.pending.grabberId !== grabber.id) {
        this.pending = { nodeId, grabberId: grabber.id, since: nowMs };
        continue;
      }

      if (nowMs - this.pending.since < GRAB_INTENT_MS) {
        continue;
      }

      this.pending = null;
      void this.acquire(nodeId, grabber);
      return;
    }
  }

  private async acquire(nodeId: string, grabber: Grabber): Promise<void> {
    const mesh = this.view.meshFor(nodeId);

    if (!mesh) {
      return;
    }

    this.requesting = true;

    try {
      const result = await this.hub.acquireNodeLock(nodeId);

      if (!result.granted) {
        // Contention is normal, not an error: show who has it and move on.
        this.onGrabRefused?.(nodeId, this.state.holderColor(nodeId));
        return;
      }

      // The server broadcasts a lock only to other peers, so the holder has to record its
      // own — otherwise your grab never highlights on your own screen.
      this.state.applyLockAcquired(nodeId, this.state.selfPeerId ?? '');

      const inverseGrabberRotation = Quaternion.Inverse(grabber.rotation);

      this.held = {
        nodeId,
        grabberId: grabber.id,
        mesh,

        // Kept as an offset in grabber space so the node holds its position and angle
        // relative to the hand, rather than snapping to it.
        grabOffset: mesh.position
          .subtract(grabber.position)
          .applyRotationQuaternion(inverseGrabberRotation),
        grabRotationOffset: inverseGrabberRotation.multiply(
          mesh.rotationQuaternion ?? Quaternion.Identity(),
        ),

        startPosition: mesh.position.clone(),
        startRotation: (mesh.rotationQuaternion ?? Quaternion.Identity()).clone(),

        // Server truth, not `this.view.toCanonical(mesh.position)` — see the field's own doc
        // comment on `Held` for why this has to be the value that was never touched by a fit
        // conversion at all.
        startCanonicalPosition: this.state.nodes.get(nodeId)?.transform.position ?? mesh.position,
      };

      this.onGrabbed?.(nodeId);
    } catch (error) {
      console.warn('Could not grab node.', error);
    } finally {
      this.requesting = false;
    }
  }

  private trackHeld(grabbers: Grabber[]): void {
    const held = this.held;

    if (held === null) {
      return;
    }

    // The node went away while it was in hand — deleted from the tray, which is now the
    // ordinary way to delete something. The server drops the lock along with the node, so
    // there is nothing to release; keeping the hold would go on streaming a dead id and
    // leave the tray offering actions for a node that no longer exists.
    if (!this.state.nodes.has(held.nodeId)) {
      this.held = null;
      this.streamer.clearNode();

      // The ordinary path (`RoomState.removeNode`) already clears the lock along with the
      // node, so this is normally a no-op — but nothing guarantees the two stay in step (a
      // `NodeDeleted` applied out of order relative to a lock event, for instance), and an
      // orphaned lock entry is an unbounded map nothing else prunes. `release()`'s own
      // sibling cleanup below performs the identical pair; do it here too rather than assume
      // the map is already clean.
      this.state.locks.delete(held.nodeId);
      this.view.onLocksChanged();

      this.onReleased?.(held.nodeId);
      return;
    }

    const grabber = grabbers.find((candidate) => candidate.id === held.grabberId);

    // Tracking loss, or the controller was put down: commit where it was rather than
    // leaving the node stuck to a hand that no longer exists.
    if (!grabber || !grabber.far.pressed) {
      void this.release();
      return;
    }

    held.mesh.position.copyFrom(
      grabber.position.add(held.grabOffset.applyRotationQuaternion(grabber.rotation)),
    );

    (held.mesh.rotationQuaternion ??= Quaternion.Identity()).copyFrom(
      grabber.rotation.multiply(held.grabRotationOffset),
    );

    // Sampled every frame, sent on the streamer's own schedule. Converted to canonical here,
    // at the point of sampling, not left as this room's own space: A2 fans `StreamNodeTransform`
    // out to every room with the diagram open (plan/diagram-extraction.md, "What A2 actually
    // cost"), and a receiver in a *different* room fits it back to its own floor
    // (`RoomView.applyLiveTransform`) — the wire format for a live pose has to carry the same
    // canonical meaning a stored one does, or two rooms of different sizes would disagree about
    // where the node is while it is still being dragged.
    this.streamer.sampleNode(
      held.nodeId,
      this.view.toCanonical(held.mesh.position),
      held.mesh.rotationQuaternion,
      held.mesh.scaling.x,
    );

    this.view.refreshEdgesFor(held.nodeId);
  }

  private async release(): Promise<void> {
    const held = this.held;

    if (held === null) {
      return;
    }

    this.held = null;
    this.streamer.clearNode();

    // The off hand is a trash can for as long as a node is held (plan/ui-improvements.md, item
    // 3) — checked exactly once, here, at the moment of release, against wherever the node and
    // the off hand actually are right now. Passing the node through the bin's own volume on the
    // way somewhere else must never delete anything; only letting go while it is there does.
    // This is why the check reads live grabber/mesh state rather than anything tracked across
    // ticks — there is nothing to reset if a carry passes through and out again, because
    // nothing was ever recorded.
    const off = this.grabbers.byRole('off');

    if (isOverTrash(off, held.mesh)) {
      try {
        await this.hub.deleteNode(held.nodeId);
        this.onNotice?.('deleted');
        this.onReleased?.(held.nodeId);
        return;
      } catch (error) {
        // The node still exists, server-side and locally, and it is still locked — deleting
        // it never happened, so there is nothing to fall through to except an ordinary
        // release. Do NOT `return` here (that used to strand the lock permanently — nothing
        // held the node any more, but nothing had released it either, and it stayed locked
        // until the connection dropped). Fall through into the same release path used below,
        // committing the node wherever the hand left it — on the bin — which is a legitimate
        // position, just not a deleted one.
        console.warn('Could not delete node.', error);
        this.onNotice?.(error instanceof Error ? error.message : String(error));
      }
    }

    const finalPosition = held.mesh.position.clone();
    const finalRotation = (held.mesh.rotationQuaternion ?? Quaternion.Identity()).clone();

    // Grabbing something and putting it back is not an edit. Checked here as well as on the
    // server so the local scene does not briefly show a version bump that never happened.
    // Compared in room space, same as `startPosition`/`finalPosition` themselves — a uniform
    // fit does not change *whether* two room-space points differ, only what canonical values
    // they correspond to.
    const moved =
      Vector3.Distance(held.startPosition, finalPosition) > MOVEMENT_DEADBAND ||
      Quaternion.Dot(held.startRotation, finalRotation) < 0.9999;

    // Canonical — what actually goes over the wire (SPEC-131). `releasePosition` is a plain
    // `Point3`, not a `Vector3`: the "not moved" branch resends `startCanonicalPosition`
    // untouched, and there is no reason to round-trip it through a `Vector3` just to convert it
    // back out again below.
    // Neither is initialised here: both branches below assign both, and an initialiser would be
    // a default that can never apply. `= finalRotation` used to sit on the second one and read
    // as "the rotation unless something changes it," which is the opposite of what happens —
    // the moved branch replaces it with the *snapped* rotation and the unmoved branch with the
    // pre-grab one, so `finalRotation` is exactly the value that never reaches the wire.
    let releasePosition: Point3;
    let releaseRotation: Quaternion;

    if (!moved) {
      held.mesh.position.copyFrom(held.startPosition);
      (held.mesh.rotationQuaternion ??= Quaternion.Identity()).copyFrom(held.startRotation);
      this.view.refreshEdgesFor(held.nodeId);

      // The exact value from before the grab, not a fresh `toCanonical` conversion — see
      // `Held.startCanonicalPosition`'s own doc comment for why a fit round trip here, even a
      // mathematically-exact one, is the wrong tool for "picking something up and putting it
      // back must not count as having moved it."
      releasePosition = held.startCanonicalPosition;
      releaseRotation = held.startRotation;
    } else {
      // Snap on release, the same rule placement uses (plan/grid.md: "if release snaps, there
      // is nothing for an alignment sweep to do" — so there is no separate align_to_grid tool
      // here, deliberately). Client-side only for this first iteration; see GridSnap.ts's own
      // module comment for what that leaves out. Occupancy excludes this node's own (pre-move)
      // cell, so releasing back near where it started never reads as "occupied by itself".
      //
      // Grid snapping stays in canonical space (SPEC-131) — `state.nodes`' own positions
      // already are canonical (RoomState mirrors server truth), so only `finalPosition` (this
      // room's own space, off the held mesh) needs converting before it joins the same
      // arithmetic.
      const canonicalDesired = this.view.toCanonical(finalPosition);
      const occupied = occupiedCellKeys(
        [...this.state.nodes.values()]
          .filter((candidate) => candidate.id !== held.nodeId)
          .map((candidate) => candidate.transform.position),
        CELL_SIZE,
      );
      const snapped = snapPlacement(
        { x: canonicalDesired.x, y: canonicalDesired.y, z: canonicalDesired.z },
        finalRotation,
        occupied,
        CELL_SIZE,
        DEFAULT_SEARCH_RADIUS_CELLS,
      );

      if (!snapped.snappedToCell) {
        this.onNotice?.('no room on the grid nearby — released without snapping');
      }

      releasePosition = snapped.position;
      releaseRotation = snapped.rotation;

      // The local mesh must land exactly where the release we are about to send says it did —
      // this class is the one place a held node is optimistic (CLAUDE.md, "only a node you
      // hold is optimistic"), so the visible jump to the snapped cell has to happen here, not
      // wait for the server's own broadcast to arrive. Fitted back to this room's own space —
      // `releasePosition` above is canonical, and the mesh renders in room space like every
      // other mesh this class touches.
      held.mesh.position.copyFrom(this.view.toRoomSpace(releasePosition));
      (held.mesh.rotationQuaternion ??= Quaternion.Identity()).copyFrom(releaseRotation);
      this.view.refreshEdgesFor(held.nodeId);
    }

    try {
      await this.hub.releaseNodeLock(held.nodeId, {
        position: { x: releasePosition.x, y: releasePosition.y, z: releasePosition.z },
        rotation: {
          x: releaseRotation.x,
          y: releaseRotation.y,
          z: releaseRotation.z,
          w: releaseRotation.w,
        },
        scale: held.mesh.scaling.x,
      });
    } catch (error) {
      console.warn('Could not release node.', error);
    }

    // Whatever the server decided arrives as NodeLockReleased; clearing the local lock here
    // keeps the node from staying faded if that event is slow.
    this.state.locks.delete(held.nodeId);
    this.view.onLocksChanged();

    this.onReleased?.(held.nodeId);
  }
}
